import { assertUnreachable } from '@hover/utilities';
import { useCallback, useReducer } from 'react';

import { useEphemeralEmail } from 'src/shared/hooks';

import type {
  AuthenticationMethod,
  AuthenticationMethodContext,
  AuthenticationMethodError,
} from '../../hooks/useAuthenticationMethod';
import { defaultValue } from '../../hooks/useAuthenticationMethod';

type State = Pick<
  AuthenticationMethodContext,
  | 'beaconLoginUrl'
  | 'singleSignOnUrl'
  | 'isBeaconEnabled'
  | 'isSingleSignOnEnabled'
>;
type Action =
  | { type: 'start'; email: string }
  | { type: 'reset' }
  | { type: 'fail'; error: AuthenticationMethodError }
  | {
      type: 'update';
      state: State;
    };

const reducer = (
  previous: AuthenticationMethodContext,
  action: Action,
): AuthenticationMethodContext => {
  switch (action.type) {
    case 'start':
      return { ...previous, email: action.email, isLoading: true };
    case 'reset':
      return { ...previous, error: undefined };
    case 'fail':
      return {
        ...previous,
        isLoading: false,
        error: action.error,
        isSingleSignOnEnabled: false,
        signInUrl: null,
      };
    case 'update': {
      const { state } = action;

      const getEnabledMethod = (): AuthenticationMethod => {
        if (state.isSingleSignOnEnabled) return 'sso'; // SSO takes precedence over Beacon Pro+
        if (state.isBeaconEnabled) return 'beacon';
        return 'password';
      };

      return {
        ...previous,
        ...state,
        signInUrl: state.singleSignOnUrl ?? state.beaconLoginUrl ?? null,
        enabledMethod: getEnabledMethod(),
        isLoading: false,
      };
    }
    default:
      return assertUnreachable(action);
  }
};

const useAuthenticationMethodState = () => {
  const { email: ephemeralEmail, setEmail: setEphemeralEmail } =
    useEphemeralEmail();
  const [context, dispatch] = useReducer(reducer, {
    ...defaultValue,
    email: ephemeralEmail,
  });

  return {
    context,
    start: useCallback(
      (email: string) => {
        setEphemeralEmail(email);
        return dispatch({ type: 'start', email });
      },
      [setEphemeralEmail],
    ),
    update: useCallback(
      (state: State) => dispatch({ type: 'update', state }),
      [],
    ),
    fail: useCallback(
      (error: AuthenticationMethodError) => dispatch({ type: 'fail', error }),
      [],
    ),
    reset: useCallback(() => dispatch({ type: 'reset' }), []),
  };
};

export { useAuthenticationMethodState };
